import { camelCase } from 'lodash';
import { CamelCase } from 'type-fest';
import { z } from 'zod';

import { Appraisal } from '../loan/Appraisal';
import { BankruptcyChapter } from '../loan/Bankruptcy';
import { Advance, Fee, FeeLineItem } from '../loan/Charges';
import { DelinquentInterestSetting } from '../loan/DelinquentInterestSetting';
import { AmortizationType, BuydownType, InterestOnlyType, ProductType } from '../loan/Loan';
import { LoanLateFee, LoanPrepaymentPenaltyFee, LoanTerminationFee } from '../loan/LoanFees';
import { AutoPaymentType, ConstructionStatus, EmailStatus, LoanReportingPaymentStatus } from '../loan/LoanReporting';
import { LoanStatus } from '../loan/LoanStatus';
import { NonPerformingReportStatus } from '../loan/NonPerformance';
import { Property } from '../loan/Property';
import { WorkoutPlanType } from '../loan/WorkoutPlan';
import { PaymentDue } from '../PaymentDue';
import { StateAbbreviation } from '../validations/fields';
import { EscrowSettingApi } from './EscrowSetting';

// Transform the LoanReporting interface so that all the enums are enforced as camelCase
export const LoanReportingApi = z.object({
  id: z.string(),
  loanId: z.string(),
  losId: z.string(),
  companyId: z.string(),

  // Additional ids for loan
  poolId: z.string().optional(),
  previousServicerId: z.string().optional(),
  capitalPartnerId: z.string().optional(),
  fullId: z.string().optional(),
  otherId1: z.string().optional(),
  otherId2: z.string().optional(),
  otherId3: z.string().optional(),
  ownerId: z.string().optional(),
  ownerLoanId: z.string().optional(),

  name: z.string().describe('Primary borrower last name, first name'),
  address: z.string(),
  addressState: StateAbbreviation,
  status: LoanStatus,
  nonPerformingStatus: NonPerformingReportStatus.transform((val) => camelCase(val) as CamelCase<typeof val>).meta({
    type: 'string',
    description: 'The non-performing status of the loan.',
    example: 'none | delinquent | defaulted',
  }),
  flagged: z.boolean(),
  amortizationType: AmortizationType,
  productType: ProductType,
  productDescription: z.string(),
  interestRate: z.number().describe('Represented as a decimal. A 5% interest rate will be returned at 0.05'),
  buydownRate: z.number().optional(),
  interestCalculationType: InterestOnlyType,
  monthlyInterestRate: z.number(),
  loanCreatedAt: z.iso.datetime(),
  hasInterestReserve: z.boolean(),
  buydownType: BuydownType.optional(),
  servicer: z.string().optional().describe('Name of the servicer if loan is pendingTransfer or transferred'),
  transferDate: z.iso.datetime().optional(),
  goodbyeEmailStatus: EmailStatus.transform((val) => camelCase(val) as CamelCase<typeof val>).meta({
    type: 'string',
    description: 'The goodbye email status of the loan.',
    example: 'none | scheduled | sent',
  }),
  mortgageeEmailStatus: EmailStatus.transform((val) => camelCase(val) as CamelCase<typeof val>).meta({
    type: 'string',
    description: 'The mortgagee email status of the loan.',
    example: 'none | scheduled | sent',
  }),
  autoPaymentType: AutoPaymentType.transform((val) => camelCase(val) as CamelCase<typeof val>).meta({
    type: 'string',
    description: 'The auto payment type of the loan.',
    example: 'none | borrowerInitiated | lenderInitiated',
  }),
  autoPaymentDayOfMonth: z.number(),
  paymentStatus: LoanReportingPaymentStatus.transform((val) => camelCase(val) as CamelCase<typeof val>).meta({
    type: 'string',
    description: 'The payment status of the loan.',
    example: 'current | pastdue | pastdue_30 | pastdue_60 | pastdue_90',
  }),
  paymentFrequency: z.number().meta({
    description: 'Number of payments per year. 12 = monthly, 1 = annual',
    example: 12,
  }),
  lastPaymentDate: z.iso.datetime().optional(),
  nextPaymentDate: z.iso.datetime().optional(),
  lastFulfilledPaymentDate: z.iso.datetime().optional(),
  paidOffDate: z.iso.datetime().optional(),
  daysPastDue: z.number(),
  daysPastGrace: z.number(),
  paystring: z.string(),
  monthlyPaymentAmount: z.number(),
  firstPaymentDueDate: z.iso.datetime(),
  closingDate: z.iso.datetime().optional(),
  fundingDate: z.iso.datetime(),
  originalPrincipalBalance: z.number(),
  outstandingPrincipalBalance: z.number(),
  principalPrePaymentBalance: z.number(),
  escrowBalance: z.number(),
  suspenseBalance: z.number(),
  reserveBalance: z.number(),
  holdbackBalance: z.number(),
  advancePrincipalBalance: z.number(),
  feesOutstanding: z.number(),
  maturityDate: z.iso.datetime().optional(),
  totalAmountDue: z.number(),
  expectAnyPayment: z.boolean(),
  firstUnfulfilledPaymentDate: z.iso.datetime().optional(),
  totalAmountCollected: z.number(),
  hasBorrowerLoggedIn: z.boolean(),
  hasBorrowerConnectedAccount: z.boolean(),
  totalCommunicationsSent: z.number(),
  gracePeriod: z.number().optional(),
  defaultInterestRate: z
    .number()
    .optional()
    .describe(
      'This rate will added to the existing current rate if the loan goes delinquent. Represented as a decimal. A 5% interest rate will be returned at 0.05',
    ),
  lienPosition: z.number().optional(),
  constructionStatus: ConstructionStatus.transform((val) => camelCase(val) as CamelCase<typeof val>).meta({
    type: 'string',
    description: 'The construction status of the loan.',
    example:
      'non_construction | construction_active | construction_to_permanent_active | construction_to_permanent_permanent',
  }),
  dutch: z.boolean(),
  entityName: z.string().optional().describe('Name of the entity on the loan'),
  workoutPlanType: WorkoutPlanType.or(z.literal('none'))
    .transform((val) => camelCase(val) as CamelCase<typeof val>)
    .meta({
      type: 'string',
      description: 'The type of workout plan the loan is on.',
      example: 'none | paymentDeferment | repaymentPlan | forbearance',
    }),
  drawsEnabled: z.boolean(),
  remainingCredit: z.number(),
  pointOfContactId: z.string().optional(),
  originalLTV: z
    .number()
    .optional()
    .describe('The original LTV of the loan. This will only be available for loans with appraisals.'),
  currentLTV: z
    .number()
    .optional()
    .describe('The current LTV of the loan. This will only be available for loans with appraisals.'),

  statements: z.array(
    PaymentDue.pick({
      total: true,
      principal: true,
      interest: true,
      escrow: true,
      reserveCredit: true,
      escrowShortage: true,
      statementDate: true,
      status: true,
      wentPastGrace: true,
      createdAt: true,
      paymentDate: true,
    }).extend({
      id: z.string(),
    }),
  ),
  fees: z.array(
    Fee.pick({
      totalAmount: true,
      amountRemaining: true,
      status: true,
      chargeDate: true,
      updatedAt: true,
      cancelledAt: true,
      comment: true,
      investorRemittanceSplit: true,
      isDeferred: true,
    }).extend({
      id: z.string(),
      lineItems: z.array(
        FeeLineItem.pick({
          amount: true,
          type: true,
          name: true,
          investorRemittanceSplit: true,
        }),
      ),
    }),
  ),
  advances: z.array(
    Advance.pick({
      totalAmount: true,
      amountRemaining: true,
      status: true,
      chargeDate: true,
      updatedAt: true,
      cancelledAt: true,
      comment: true,
      investorRemittanceSplit: true,
      isDeferred: true,
      advanceConfig: true,
      advanceInterestUpdatedAt: true,
      recipient: true,
      advanceInterest: true,
      advancePrincipal: true,
      originalPrincipal: true,
    }).extend({
      id: z.string(),
    }),
  ),
  properties: z.array(
    Property.pick({
      address: true,
      isReleased: true,
      coverageAmount: true,
      replacementCostValue: true,
      propertyType: true,
      propertySubType: true,
      flood: true,
      purchasePrice: true,
      numberOfUnits: true,
      propertyValuationMethodType: true,
      transferableMonitoringContracts: true,
    }).extend({ id: z.string() }),
  ),
  appraisals: z.array(Appraisal.pick({ amount: true, date: true }).extend({ id: z.string(), propertyId: z.string() })),
  escrowSettings: z.array(EscrowSettingApi),
  defaultInterestSettings: z.array(
    DelinquentInterestSetting.pick({
      startDate: true,
      endDate: true,
      interestRate: true,
      isDeferred: true,
      isWaived: true,
      investorRemittanceSplit: true,
    }).extend({
      id: z.string(),
    }),
  ),
  prepaymentPenaltySettings: LoanPrepaymentPenaltyFee.optional(),
  lateFeeSettings: LoanLateFee.optional(),
  terminationFeeSettings: LoanTerminationFee.optional(),
  bankruptcyChapter: BankruptcyChapter.optional(),
});
export type LoanReportingApi = z.infer<typeof LoanReportingApi>;
